import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTour } from '../contexts/TourContext';
import './GuidedTour.css';
import { TabType } from '../contexts/NavigationContext';
import { useClassroomData } from '../data/classroomData';


export interface TourStep {
	targetSelector: string;
	content: string; // This can now contain HTML
	position: 'top' | 'right' | 'bottom' | 'left';
	spotlightRadius?: number;
	tooltipWidth?: number;
	waitForElement?: boolean;
	forceTooltipVisible?: boolean;
	showTooltip?: boolean;  // Flag to indicate if we need to show a tooltip for this element
	switchTab?: TabType; // Tab to switch to before showing the tooltip
	showEventSelector?: string; // Selector for an element to click before showing the tooltip
}

interface GuidedTourProps {
}

const GuidedTour: React.FC<GuidedTourProps> = () => {
	const {
		activeTourStep,
		isTourActive,
		nextStep: originalNextStep,
		previousStep: originalPreviousStep,
		endTour: originalEndTour,
		steps
	} = useTour();
	
	const [targetElements, setTargetElements] = useState<HTMLElement[]>([]);
	const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
	const [spotlightPositions, setSpotlightPositions] = useState<Array<{
		top: number;
		left: number;
		width: number;
		height: number;
	}>>([]);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const [tooltipVisible, setTooltipVisible] = useState(false);
	const lastStepRef = useRef<number>(-1);
	const svgRef = useRef<SVGSVGElement>(null);
	const nextButtonRef = useRef<HTMLButtonElement>(null);


	const classroomQueryData = useClassroomData().data;    
	
	// Wrap the original navigation functions to handle tooltip cleanup
	const closeActiveTooltips = () => {
		console.log('Closing active tooltips before navigation');
		
		// First, dispatch stayClose events to all tooltip containers
		document.querySelectorAll('.tooltip-container').forEach(container => {
			container.dispatchEvent(new CustomEvent('stayClose', {
				bubbles: true,
				cancelable: true
			}));
		});
	};

	const switchToTab = (tab:string) => {
		// Find and click the tab button
		const tabSelector = `.tab-button`;
		const tabButtons = document.querySelectorAll(tabSelector);
		
		tabButtons.forEach(button => {
			// Check if button text contains the tab name
			if (
				(tab === 'staff' && button.textContent?.includes('Missing')) ||
				(tab === 'details' && button.textContent?.includes('Classroom Details')) ||
				(tab === 'classrooms' && button.textContent?.includes('Classroom Summary')) ||
				(tab === 'historical' && button.textContent?.includes('Historical Ratios'))
			) {
				(button as HTMLButtonElement).click();
			}
		});
	}

	const nextStep = () => {
		closeActiveTooltips();
		const nextTab = steps[activeTourStep+1].switchTab
		if (nextTab ){
			switchToTab(nextTab);
		}
		originalNextStep();
	};

	const previousStep = () => {
		closeActiveTooltips();
		const nextTab = steps[activeTourStep-1].switchTab
		if (nextTab ){
			switchToTab(nextTab);
		}
		originalPreviousStep();
	};

	const endTour = () => {
		closeActiveTooltips();
		originalEndTour();
	};

	// Add effect to manage body class for overlay
	useEffect(() => {
		if (isTourActive) {
			document.body.classList.add('guided-tour-active');
		} else {
			document.body.classList.remove('guided-tour-active');
		}
		
		return () => {
			document.body.classList.remove('guided-tour-active');
		};
	}, [isTourActive]);

	useLayoutEffect(() => {
		// If tour is not active, don't attempt to find elements
		if (!isTourActive || activeTourStep < 0 || activeTourStep >= steps.length) {
			return;
		}

		// Clean up tooltips when changing steps
		if (lastStepRef.current !== activeTourStep) {
			closeActiveTooltips();
		}

		// Keep track of step changes
		lastStepRef.current = activeTourStep;

		// Define a function to find and position based on the target elements
		const positionBasedOnTargets = async () => {
			const step = steps[activeTourStep];
			
			if (!step) return;

			// fire show-events for any showEventsSelector elements
			if (step.showEventSelector) {
				const showElements = Array.from(document.querySelectorAll(step.showEventSelector)) as HTMLElement[];
				showElements.forEach(element => {
					element.dispatchEvent(new CustomEvent('tour-show', {
						bubbles: true,
						cancelable: true
					}));
				});
			}

			// Find all target elements
			const elements = Array.from(document.querySelectorAll(step.targetSelector)) as HTMLElement[];
			

			setTargetElements(elements);
			
			// Trigger events on all tooltip containers
			elements.forEach(element => {
				const tooltipContainer = element.closest('.tooltip-container') || element;
				
				if (step.showTooltip || step.forceTooltipVisible) {          
					// Dispatch a custom event that tooltip components can listen for
					tooltipContainer.dispatchEvent(new CustomEvent('stayOpen', {
						bubbles: true,
						cancelable: true
					}));

					//add tooltip to the elements array
					elements.push(tooltipContainer as HTMLElement);
				}
			});
			
			// Update positions for all elements
			setTimeout(async () => {
				// need to re-search for elements in case they were added to the DOM after the initial search
				const elements = Array.from(document.querySelectorAll(step.targetSelector)) as HTMLElement[];
				await updatePositions(elements, step);
				setTooltipVisible(true);
			}, 100);
			
			return () => {};
		};

		// Reset tooltip visibility when changing steps
		setTooltipVisible(false);
		
		const cleanup = positionBasedOnTargets();

		// Set up a window resize listener to reposition
		const handleResize = () => {
			if (targetElements.length > 0) {
				updatePositions(targetElements, steps[activeTourStep]);
			}
		};

		window.addEventListener('resize', handleResize);
		
		// Hide anything that was shown as part of the tour
		// Clean up event listener and timeout on unmount or when step changes
		return () => {
			if (step.showEventSelector) {
				const showElements = Array.from(document.querySelectorAll(step.showEventSelector)) as HTMLElement[];
				showElements.forEach(element => {
					element.dispatchEvent(new CustomEvent('tour-hide', {
						bubbles: true,
						cancelable: true
					}));
				});
			}

			window.removeEventListener('resize', handleResize);
			if (cleanup) cleanup.then(func => func && func());
		};
	}, [isTourActive, activeTourStep, steps, classroomQueryData]);

	// Focus the "Next" button when the tour starts or step changes (if not last step)
	useEffect(() => {
		if (!isTourActive) return;
		// Only focus if not on the last step (where "Finish" is shown)
		if (activeTourStep < steps.length - 1 && nextButtonRef.current) {
			nextButtonRef.current.focus();
		}
	}, [isTourActive, activeTourStep, steps.length]);

	const updatePositions = async (elements: HTMLElement[], step: TourStep): Promise<void> => {
		// Calculate positions for all elements
		const positions = elements.map(element => {
			const rect = element.getBoundingClientRect();
			const margin = 10; // 10px margin around the element
			
			return {
				top: rect.top + window.scrollY - margin,
				left: rect.left + window.scrollX - margin,
				width: rect.width + margin * 2,
				height: rect.height + margin * 2,
				element
			};
		});
		
		setSpotlightPositions(positions);

		// For tooltip positioning, use the first element or the element closest to center
		const primaryElement = elements[0];
		
		// Return a promise that resolves after the tooltip is positioned
		return new Promise<void>((resolve) => {
			setTimeout(() => {
				if (tooltipRef.current && primaryElement) {
					const tooltipRect = tooltipRef.current.getBoundingClientRect();
					const rect = primaryElement.getBoundingClientRect();
					
					let tooltipTop = 0;
					let tooltipLeft = 0;
					
					const padding = 20; // padding between spotlight and tooltip
					
					switch (step.position) {
						case 'top':
							tooltipTop = rect.top + window.scrollY - tooltipRect.height - padding;
							tooltipLeft = rect.left + window.scrollX + (rect.width - tooltipRect.width) / 2;
							break;
						case 'right':
							tooltipTop = rect.top + window.scrollY + (rect.height - tooltipRect.height) / 2;
							tooltipLeft = rect.right + window.scrollX + padding;
							break;
						case 'bottom':
							tooltipTop = rect.bottom + window.scrollY + padding;
							tooltipLeft = rect.left + window.scrollX + (rect.width - tooltipRect.width) / 2;
							break;
						case 'left':
							tooltipTop = rect.top + window.scrollY + (rect.height - tooltipRect.height) / 2;
							tooltipLeft = rect.left + window.scrollX - tooltipRect.width - padding;
							break;
					}
					
					// Keep tooltip within viewport bounds
					const viewportWidth = window.innerWidth;
					const viewportHeight = window.innerHeight;
					
					if (tooltipLeft < 0) tooltipLeft = 10;
					if (tooltipLeft + tooltipRect.width > viewportWidth) tooltipLeft = viewportWidth - tooltipRect.width - 10;
					if (tooltipTop < 0) tooltipTop = 10;
					if (tooltipTop + tooltipRect.height > viewportHeight + window.scrollY) 
						tooltipTop = viewportHeight + window.scrollY - tooltipRect.height - 10;
					
					setTooltipPosition({ top: tooltipTop, left: tooltipLeft });
				}
				resolve();
			}, 100);
		});
	};

	if (!isTourActive || activeTourStep < 0 || activeTourStep >= steps.length ) {
		return null;
	}

	const step = steps[activeTourStep];
	const borderRadius = 8; // Border radius for the cutout

	return (
		<div className="guided-tour-component guided-tour-overlay">
			{/* SVG overlay with mask for multiple elements */}
			<svg 
				ref={svgRef}
				className="guided-tour-svg-overlay"
				width="100%"
				height="100%"
				xmlns="http://www.w3.org/2000/svg"
				preserveAspectRatio="none"
			>
				<defs>
					<mask id="spotlight-mask">
						{/* White background (fully opaque) */}
						<rect x="0" y="0" width="100%" height="100%" fill="white" />
						
						{/* Black cutouts (transparent) for all matching elements */}
						{spotlightPositions.map((pos, index) => (
							<rect 
								key={index}
								x={pos.left}
								y={pos.top}
								width={pos.width}
								height={pos.height}
								rx={borderRadius}
								ry={borderRadius}
								fill="black"
							/>
						))}
					</mask>
				</defs>
				
				{/* Darkened area using the mask */}
				<rect 
					x="0" 
					y="0" 
					width="100%" 
					height="100%" 
					fill="rgba(0, 0, 0, 0.7)"
					mask="url(#spotlight-mask)"
				/>
			</svg>
			
			{/* Tooltip */}
			{true && (
				<div 
					ref={tooltipRef}
					className={`guided-tour-tooltip guided-tour-tooltip-${step.position}`}
					style={{
						top: tooltipPosition.top,
						left: tooltipPosition.left,
						width: step.tooltipWidth || 300
					}}
					>
					<div 
						className="guided-tour-tooltip-content" 
						dangerouslySetInnerHTML={{ __html: step.content }}
					/>
					<div className="guided-tour-tooltip-navigation">
						<div className="guided-tour-tooltip-step-counter">
							Step {activeTourStep + 1} of {steps.length}
						</div>
						<div className="guided-tour-tooltip-buttons">
							{activeTourStep > 0 && (
								<button 
									className="guided-tour-tooltip-button guided-tour-tooltip-button-prev"
									onClick={previousStep}
								>
									Previous
								</button>
							)}
							{activeTourStep < steps.length - 1 ? (
								<button 
									className="guided-tour-tooltip-button guided-tour-tooltip-button-next"
									onClick={nextStep}
									ref={nextButtonRef}
								>
									Next
								</button>
							) : (
								<button 
									className="guided-tour-tooltip-button guided-tour-tooltip-button-finish"
									onClick={endTour}
								>
									Finish
								</button>
							)}
						</div>
					</div>
					<button 
						className="guided-tour-tooltip-close"
						onClick={endTour}
						aria-label="Close tour"
					>
						×
					</button>
				</div>
			)}
			
			<div className="guided-tour-background-mask" onClick={(e) => {e.preventDefault(); e.stopPropagation()}} />
		</div>
	);
};

export default GuidedTour;